// ==================== src/store/slices/liveAffiliatesSlice.js ====================
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Async thunk to fetch live affiliates from the dedicated live-feed endpoint
export const fetchLiveAffiliates = createAsyncThunk(
  'liveAffiliates/fetchLiveAffiliates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/keyword-affiliate/live-feed`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (result.success) {
        return result.data || [];
      } else {
        return rejectWithValue(result.message || 'Failed to fetch live affiliates');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to trigger processing of new affiliates
export const processNewAffiliate = createAsyncThunk(
  'liveAffiliates/processNewAffiliate',
  async (limit = 1, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/keyword-affiliate/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ limit })
      });

      const result = await response.json();

      if (result.success) {
        return result.data || [];
      } else {
        return rejectWithValue(result.message || 'Failed to process affiliate');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  selectedBrand: {
    brand_url: '',
    brand_name: '',
    brand_id: null,
    country: '',
    vertical: ''
  },
  affiliates: [],
  liveFeedAffiliates: [], // New state for live feed data
  lastFetchTime: null,
  loading: false,
  processing: false,
  error: null
};

const liveAffiliatesSlice = createSlice({
  name: 'liveAffiliates',
  initialState,
  reducers: {
    setSelectedBrand: (state, action) => {
      state.selectedBrand = action.payload;
    },
    clearSelectedBrand: (state) => {
      state.selectedBrand = initialState.selectedBrand;
    },
    setAffiliates: (state, action) => {
      state.affiliates = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    addNewAffiliate: (state, action) => {
      // Add new affiliate to the beginning of liveFeedAffiliates
      const newAffiliate = action.payload;
      const exists = state.liveFeedAffiliates.some(
        a => a.affiliate_url === newAffiliate.affiliate_url
      );
      if (!exists) {
        state.liveFeedAffiliates = [newAffiliate, ...state.liveFeedAffiliates].slice(0, 20);
      }
    },
    clearLiveFeed: (state) => {
      state.liveFeedAffiliates = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchLiveAffiliates cases
      .addCase(fetchLiveAffiliates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLiveAffiliates.fulfilled, (state, action) => {
        state.loading = false;
        state.liveFeedAffiliates = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchLiveAffiliates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // processNewAffiliate cases
      .addCase(processNewAffiliate.pending, (state) => {
        state.processing = true;
      })
      .addCase(processNewAffiliate.fulfilled, (state, action) => {
        state.processing = false;
        // Add processed affiliates to the live feed
        if (action.payload && action.payload.length > 0) {
          action.payload.forEach(newAffiliate => {
            const exists = state.liveFeedAffiliates.some(
              a => a.affiliate_url === newAffiliate.affiliate_url
            );
            if (!exists) {
              state.liveFeedAffiliates = [newAffiliate, ...state.liveFeedAffiliates].slice(0, 20);
            }
          });
        }
      })
      .addCase(processNewAffiliate.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload;
      });
  }
});

export const {
  setSelectedBrand,
  clearSelectedBrand,
  setAffiliates,
  setLoading,
  setError,
  addNewAffiliate,
  clearLiveFeed
} = liveAffiliatesSlice.actions;

// Selectors
export const selectLiveFeedAffiliates = (state) => state.liveAffiliates.liveFeedAffiliates;
export const selectIsProcessing = (state) => state.liveAffiliates.processing;
export const selectIsLoading = (state) => state.liveAffiliates.loading;
export const selectLastFetchTime = (state) => state.liveAffiliates.lastFetchTime;

export default liveAffiliatesSlice.reducer;
